/**
 * AdminUsers — paginated viewer + search + per-user MVP-test actions.
 *
 * Read-only browse of every registered user. Surfaces signup date, email
 * verified state, opt-in flag, timezone, last email sent. Lets the
 * operator:
 *   • Search by email/name
 *   • Sort by recency / oldest / last email / email A-Z
 *   • Export the full table to CSV
 *   • Per-user: "Resend welcome" / "Mark email verified" / "View trace"
 *
 * Intentionally English-only (internal admin tool, not Phase-2 i18n surface).
 */
/* eslint-disable */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Download, MailCheck, MailPlus, ShieldAlert,
  RefreshCw, ChevronLeft, ChevronRight, Activity, ClipboardCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 25;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
    amber: 'bg-amber-100 text-amber-700',
    sky: 'bg-sky-100 text-sky-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_at_desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState({}); // { user_id: 'resend'|'verify' }

  useEffect(() => {
    if (user && user !== null && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: PAGE, offset, sort });
      if (search.trim()) params.set('search', search.trim());
      const { data } = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401) setError('Please sign in as an admin.');
      else if (s === 403) setError('Admin access required.');
      else setError('Could not load users.');
    } finally {
      setLoading(false);
    }
  }, [offset, search, sort]);

  useEffect(() => { load(); }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setOffset(0);
    load();
  };

  const handleSortChange = (e) => {
    setSort(e.target.value);
    setOffset(0);
  };

  const handleResendWelcome = async (u) => {
    setActionBusy((m) => ({ ...m, [u.id]: 'resend' }));
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${u.id}/resend-welcome`, {}, { withCredentials: true },
      );
      if (data?.status === 'sent') toast.success(`Welcome email re-sent to ${u.email}`);
      else if (data?.status === 'skipped') toast.warning('Nudge emails are disabled in this environment (NUDGE_EMAILS_ENABLED=false)');
      else toast.error(`Resend failed (status: ${data?.status})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend welcome failed');
    } finally {
      setActionBusy((m) => { const c = { ...m }; delete c[u.id]; return c; });
    }
  };

  const handleMarkVerified = async (u) => {
    setActionBusy((m) => ({ ...m, [u.id]: 'verify' }));
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${u.id}/mark-email-verified`, {}, { withCredentials: true },
      );
      if (data?.already_verified) toast.info(`${u.email} was already verified`);
      else toast.success(`${u.email} marked as verified`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark verified failed');
    } finally {
      setActionBusy((m) => { const c = { ...m }; delete c[u.id]; return c; });
    }
  };

  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin')}
              aria-label="Back to admin"
              data-testid="admin-users-back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Users</h1>
              <p className="text-[11px] text-gray-500">
                {loading ? 'loading…' : `${total} registered`}
                {search ? ` · matching "${search}"` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/admin/users/export.csv`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
              data-testid="admin-users-export-csv"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </a>
            <Button
              variant="outline" size="sm"
              onClick={() => load()}
              data-testid="admin-users-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {/* Search + Sort */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm mb-4">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by email or name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="admin-users-search-input"
                />
              </div>
              <Button type="submit" size="sm" data-testid="admin-users-search-submit">Search</Button>
            </form>
            <select
              value={sort}
              onChange={handleSortChange}
              className="h-10 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700"
              data-testid="admin-users-sort"
            >
              <option value="created_at_desc">Newest first</option>
              <option value="created_at_asc">Oldest first</option>
              <option value="last_email_desc">Last email sent</option>
              <option value="email_asc">Email A-Z</option>
            </select>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm p-3 mb-4 flex items-center gap-2" data-testid="admin-users-error">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Table */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm overflow-hidden" data-testid="admin-users-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-gray-500 text-left">
                  <tr>
                    <th className="font-medium py-2.5 px-3">User</th>
                    <th className="font-medium py-2.5 px-3 hidden md:table-cell">Provider</th>
                    <th className="font-medium py-2.5 px-3 hidden lg:table-cell">Timezone</th>
                    <th className="font-medium py-2.5 px-3 hidden md:table-cell">Signed up</th>
                    <th className="font-medium py-2.5 px-3 hidden lg:table-cell">Last email</th>
                    <th className="font-medium py-2.5 px-3">State</th>
                    <th className="font-medium py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {items.length === 0 && !loading ? (
                    <tr><td colSpan={7} className="py-8 text-center text-gray-500">No users match.</td></tr>
                  ) : items.map((u) => {
                    const busy = actionBusy[u.id];
                    return (
                      <tr key={u.id} className="border-t border-gray-50 hover:bg-slate-50/50" data-testid={`admin-users-row-${u.id}`}>
                        <td className="py-2.5 px-3">
                          <p className="font-medium text-gray-900 truncate max-w-[220px]">{u.email || '—'}</p>
                          {u.name && <p className="text-[11px] text-gray-500 truncate max-w-[220px]">{u.name}</p>}
                        </td>
                        <td className="py-2.5 px-3 hidden md:table-cell">
                          <Pill tone={u.auth_provider === 'google' ? 'sky' : 'slate'}>{u.auth_provider || '—'}</Pill>
                          {u.role === 'admin' && <span className="ml-1"><Pill tone="amber">admin</Pill></span>}
                        </td>
                        <td className="py-2.5 px-3 hidden lg:table-cell">
                          <span className={u.timezone && u.timezone !== 'UTC' ? 'text-gray-700' : 'text-amber-600'}>
                            {u.timezone || '—'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 hidden md:table-cell text-gray-600 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                        <td className="py-2.5 px-3 hidden lg:table-cell text-gray-600">
                          {u.last_email_sent_at ? (
                            <>
                              <p className="whitespace-nowrap">{fmtDate(u.last_email_sent_at)}</p>
                              <p className="text-[10px] text-gray-400">{u.last_email_type || ''}</p>
                            </>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex flex-wrap gap-1">
                            {u.email_verified ? <Pill tone="emerald">verified</Pill> : <Pill tone="amber">unverified</Pill>}
                            {!u.nudge_emails_opt_in && <Pill tone="rose">opted-out</Pill>}
                            {u.onboarding_completed ? <Pill tone="indigo">onboarded</Pill> : null}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            <button
                              onClick={() => navigate(`/admin/email-trace?email=${encodeURIComponent(u.email)}`)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 text-[11px] text-gray-700 hover:bg-gray-50 transition"
                              data-testid={`admin-users-view-trace-${u.id}`}
                              title="View email delivery trace"
                            >
                              <Activity className="h-3 w-3" />
                              Trace
                            </button>
                            <button
                              onClick={() => navigate(`/admin/users/${u.id}/sanity`)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 text-[11px] text-gray-700 hover:bg-gray-50 transition"
                              data-testid={`admin-users-sanity-${u.id}`}
                              title="View 7-day check-in data sanity grid"
                            >
                              <ClipboardCheck className="h-3 w-3" />
                              Sanity
                            </button>
                            <button
                              onClick={() => handleResendWelcome(u)}
                              disabled={!!busy}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-[11px] text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-50"
                              data-testid={`admin-users-resend-welcome-${u.id}`}
                              title="Re-send the onboarding welcome email"
                            >
                              <MailPlus className="h-3 w-3" />
                              {busy === 'resend' ? '…' : 'Resend welcome'}
                            </button>
                            {!u.email_verified && (
                              <button
                                onClick={() => handleMarkVerified(u)}
                                disabled={!!busy}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-indigo-200 bg-indigo-50 text-[11px] text-indigo-700 hover:bg-indigo-100 transition disabled:opacity-50"
                                data-testid={`admin-users-verify-${u.id}`}
                                title="Mark this user's email as verified"
                              >
                                <MailCheck className="h-3 w-3" />
                                {busy === 'verify' ? '…' : 'Mark verified'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
          <p data-testid="admin-users-page-info">Page {page} of {pages} · Showing {items.length} of {total}</p>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              data-testid="admin-users-prev"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Prev
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={offset + items.length >= total || loading}
              onClick={() => setOffset(offset + PAGE)}
              data-testid="admin-users-next"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
